import { useCallback, useEffect, useState } from 'react';
import { Form, InputGroup } from 'react-bootstrap';
import { Button } from '@Components/Button';
import { Dropdown } from '@Components/Dropdown';
import { DropdownOption } from '@Components/Dropdown/dropdown-constants';
import { InfoTooltip } from '@Components/InfoTooltip';
import { Background, Size } from '@Components/designConstants';
import { WidgetContainer } from '@Components/WidgetContainer';
import {
  awaitJSON,
  SheetsLambdaFetch,
  StreamingUtilsLambdaFetch
} from '../../../utils/fetch-utils';
import { AdOpsToolProps } from '../adops-constants';

// Digits only, so typing a non-number never reaches the backend as NaN/null.
const parseId = (value: string): number | null => {
  const digits = value.replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : null;
};

export const ExtremeReachCampaignAdder = ({ utils }: AdOpsToolProps) => {
  const [activeAPICall, setActiveAPICall] = useState(false);
  const [advertiserId, setAdvertiserId] = useState<number | null>(null);
  const [advertiserName, setAdvertiserName] = useState('');
  const [campaignId, setCampaignId] = useState<number | null>(null);
  const [campaignName, setCampaignName] = useState('');
  const [company, setCompany] = useState('');
  const [companyOptions, setCompanyOptions] = useState<string[]>();
  const { setError } = utils;

  useEffect(() => {
    if (!companyOptions) {
      (async () => {
        try {
          const res = await SheetsLambdaFetch('/get_company_ids');
          const companies = await awaitJSON<{ ids: string[] }>(res);
          setCompanyOptions(companies.ids);
        } catch (e) {
          setError({ message: e.message, reportError: e });
        }
      })();
    }
  }, [companyOptions, setError]);

  const companyDropdownOptions: DropdownOption[] = (companyOptions ?? []).map(value => ({
    value,
    label: value
  }));

  // Company, advertiser name and ID are required. The campaign is optional, but its name and ID
  // must be given together (the lambda rejects one without the other).
  const hasCampaignName = campaignName.trim() !== '';
  const hasCampaignId = campaignId !== null;
  const canSubmit =
    company !== '' &&
    advertiserName.trim() !== '' &&
    advertiserId !== null &&
    hasCampaignName === hasCampaignId;

  const addERAdvertiserAndCampaign = useCallback(async () => {
    try {
      setActiveAPICall(true);
      await StreamingUtilsLambdaFetch('/addERAdvertiserAndCampaign', {
        method: 'POST',
        body: JSON.stringify({
          company,
          advertiserName,
          advertiserId,
          campaignName,
          campaignId
        })
      });
      setCompany('');
      setAdvertiserName('');
      setAdvertiserId(null);
      setCampaignName('');
      setCampaignId(null);
    } catch (e) {
      setError({
        message: e.message,
        reportError: e
      });
    }
    setActiveAPICall(false);
  }, [company, advertiserName, advertiserId, campaignName, campaignId, setError]);

  return (
    <WidgetContainer
      className='linear-tool-card'
      header={
        <>
          Add Extreme Reach Campaign
          <InfoTooltip interactive size={Size.MD}>
            The company you input should be the all lowercased company ID used in master_companies
            and in our app URLs.
            <br />
            <br />
            You can find the Advertiser Name and ID by selecting your client{' '}
            <a
              href='https://app.extremereach.com/adbridge/dist/Home/Index'
              target='_blank'
              rel='noopener noreferrer'
            >
              here
            </a>
            . Once you find your client, you should see their advertiser ID in the page&apos;s URL
            as &quot;advertiserId=&quot;.
            <br />
            <br />
            You can find the Campaign Name and ID by searching for your client{' '}
            <a
              href='https://app.extremereach.com/vid/traffic/mediaplans/search'
              target='_blank'
              rel='noopener noreferrer'
            >
              here
            </a>
            .
          </InfoTooltip>
        </>
      }
      background={Background.DARK}
      dataTestId='er-campaign'
    >
      <div className='ad-ops-elem' data-testid='erc-company'>
        <Dropdown
          size={Size.MD}
          label='Company'
          menuPortalTarget={document.body}
          placeholder='Select or type a company…'
          searchable
          creatable
          clearable
          value={company || null}
          options={companyDropdownOptions}
          onChange={value => setCompany(value ?? '')}
          reactSelectProps={{ formatCreateLabel: input => `Use "${input}"` }}
        />
      </div>
      <InputGroup className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Advertiser Name</InputGroup.Text>
        <Form.Control
          value={advertiserName}
          onChange={e => setAdvertiserName(e.target.value)}
          data-testid='erc-advertiser-name'
        />
        <InputGroup.Text className='input-label'>Advertiser Id</InputGroup.Text>
        <Form.Control
          value={advertiserId ?? ''}
          onChange={e => setAdvertiserId(parseId(e.target.value))}
          data-testid='erc-advertiser-id'
        />
      </InputGroup>
      <InputGroup className='ad-ops-elem'>
        <InputGroup.Text className='input-label'>Campaign Name</InputGroup.Text>
        <Form.Control
          value={campaignName}
          onChange={e => setCampaignName(e.target.value)}
          data-testid='erc-campaign-name'
        />
        <InputGroup.Text className='input-label'>Campaign Id</InputGroup.Text>
        <Form.Control
          value={campaignId ?? ''}
          onChange={e => setCampaignId(parseId(e.target.value))}
          data-testid='erc-campaign-id'
        />
      </InputGroup>
      <Button
        className='ad-ops-elem'
        loading={activeAPICall}
        disabled={!canSubmit}
        onClick={addERAdvertiserAndCampaign}
        data-testid='er-campaign-add'
      >
        Add
      </Button>
    </WidgetContainer>
  );
};
